import type { CaseEvent } from '@core/domain/model';
import { daysBetween, timingOf, timingText } from '@core/ui/TaskTiming';
import type { TaskListItem, TasksListEntry, TasksTab } from './types';

/**
 * Группировка задач по вкладкам — порт `entriesFor` из `TasksViewModel`.
 *
 * Чистая функция: те же случаи, что в Android-тестах, проверяются здесь Vitest'ом
 * без базы и без React.
 */

/** Срок задачи — это её дедлайн, а у заседания и встречи — время начала. */
export const momentOf = (event: CaseEvent): number => event.deadlineAt ?? event.startAt;

export const isOverdue = (event: CaseEvent, now: number): boolean =>
  !event.isCompleted && momentOf(event) < now;

export function toItem(event: CaseEvent, caseTitle: string | null, now: number): TaskListItem {
  const accent = timingOf(momentOf(event), event.isCompleted, now);

  return {
    id: event.id,
    title: event.title,
    timing: timingText(momentOf(event), accent),
    timingAccent: accent,
    caseId: event.caseId,
    caseTitle,
    isCompleted: event.isCompleted,
    deadlineAt: momentOf(event),
  };
}

/**
 * Насколько дней вперёд заглядывает «Сегодня».
 *
 * Два дня, а не неделя: заглядывание должно помещаться под сегодняшним списком
 * целиком, иначе оно перестаёт быть заглядыванием и становится вторым списком.
 */
const PEEK_DAYS = 2;

/**
 * «Сегодня» — это просроченное, сегодняшнее и сделанное за день, тремя группами.
 *
 * Просроченное стоит первым: оно и есть причина открыть этот экран. Выполненное
 * не исчезает до конца дня — вычеркнутая строка показывает, что день не прошёл зря,
 * и позволяет вернуть отметку, поставленную по ошибке.
 *
 * Четвёртой группой — «Ближайшие дни» приглушённым: завтра и послезавтра. Это
 * не задачи вкладки, а взгляд за её край, и набраны они серым именно поэтому.
 * Просьба юриста после первого дня работы.
 */
export function entriesFor(
  tab: TasksTab,
  events: CaseEvent[],
  caseTitles: Map<string, string>,
  now: number,
): TasksListEntry[] {
  const onDay = (event: CaseEvent, shift: number) => daysBetween(now, momentOf(event)) === shift;

  const groups: { title: string; isOverdue: boolean; muted?: boolean; items: CaseEvent[] }[] =
    tab === 'today'
      ? [
          { title: 'Просрочено', isOverdue: true, items: events.filter((e) => isOverdue(e, now)) },
          {
            title: 'Сегодня',
            isOverdue: false,
            items: events.filter((e) => !e.isCompleted && !isOverdue(e, now) && onDay(e, 0)),
          },
          {
            title: 'Выполнено',
            isOverdue: false,
            items: events.filter((e) => e.isCompleted && onDay(e, 0)),
          },
          {
            title: 'Ближайшие дни',
            isOverdue: false,
            muted: true,
            items: events.filter((e) => {
              const away = daysBetween(now, momentOf(e));
              return !e.isCompleted && away >= 1 && away <= PEEK_DAYS;
            }),
          },
        ]
      : tab === 'tomorrow'
        ? [
            {
              title: 'Завтра',
              isOverdue: false,
              items: events.filter((e) => !e.isCompleted && onDay(e, 1)),
            },
          ]
        : [
            {
              title: 'Ближайшие',
              isOverdue: false,
              items: events.filter(
                (e) => !e.isCompleted && daysBetween(now, momentOf(e)) > 1,
              ),
            },
          ];

  const filled = groups.filter((group) => group.items.length > 0);

  return filled.flatMap((group): TasksListEntry[] => {
    const sorted = [...group.items].sort((a, b) => momentOf(a) - momentOf(b));
    const rows = sorted.map(
      (event): TasksListEntry => ({
        kind: 'item',
        task: toItem(event, caseTitles.get(event.caseId ?? '') ?? null, now),
        muted: group.muted,
      }),
    );

    // Один заголовок в списке — лишний шум: он просто повторял бы вкладку. Но
    // у заглядывания вперёд заголовок остаётся всегда: без него завтрашние задачи
    // на вкладке «Сегодня» читаются как сегодняшние.
    return filled.length > 1 || group.muted
      ? [
          {
            kind: 'header',
            title: group.title,
            count: sorted.length,
            isOverdue: group.isOverdue,
            muted: group.muted,
          },
          ...rows,
        ]
      : rows;
  });
}
